/**
 * Phase 45 Plan 45-08 (D-14) — TaskDetailSlideOver watchers section.
 * Widened by quick 260729-cj9: add-watcher picker + per-row remove.
 *
 * Lists watchers and lets the current user self-watch / self-unwatch via
 * the /me sugar endpoints. Internal audience additionally offers:
 *   - an "Add watcher" searchable picker over the company-wide
 *     watcher-candidates endpoint (any active internal user, NOT scoped to
 *     the task's project — mirrors the widened backend addWatcher policy).
 *   - a per-row × remove affordance on every internal watcher.
 * Add/remove of other users is still gated server-side (Q6 asymmetric
 * matrix: admin/PM/member may add anyone; only self/admin/PM may remove);
 * a disallowed action surfaces via the toast branch in useTaskWatchers.
 * External audience is unchanged (self-watch toggle only, no picker/×).
 *
 * Note on currentUserId resolution: this component reads the auth context
 * directly via useAuth so the parent (TaskDetailSlideOver) does NOT need
 * to thread the id down. Matches the pattern used by CommentList /
 * CommentComposer in the same file.
 */
import { useEffect, useRef, useState } from 'react';
import { EyeIcon, EyeSlashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  useTaskWatchers,
  type WatcherDto,
} from '../../hooks/useTaskWatchers';
import { useWatcherCandidates } from '../../hooks/useWatcherCandidates';
import { useAuth } from '../../services/authContext';
import { useExternalCurrentUser } from '../../hooks/useExternalCurrentUser';
import { Avatar } from '../ui/Avatar';

interface TaskWatchersSectionProps {
  taskId: number;
  /**
   * external-board-redirect-login (2026-07-02) — external audience routes the
   * self-watch toggle + list to the external endpoints (cookie creds); internal
   * audience keeps the internal client. Defaults to 'internal' for back-compat.
   */
  audience?: 'internal' | 'external';
}

function displayName(user: WatcherDto['user']): string {
  const parts = [user.lastName, user.firstName].filter(Boolean);
  if (parts.length > 0) return parts.join(' ');
  return user.email;
}

/**
 * Name handed to `<Avatar>` — deliberately NOT `displayName()`.
 *
 * Avatar takes its initials from this string, and falls back to hashing it for
 * the colour whenever `colorSeq` is null (external watchers, and anyone outside
 * the project's avatar scope). The board passes `taskCardSerializer`'s
 * `assigneeName`, which is `"First Last"`, so matching it here keeps both the
 * initials and that fallback colour identical across the two surfaces. The
 * chip's visible label stays `"Last First"` per the Czech UI convention, so
 * these two intentionally disagree. Mirrors backend `formatUserName`.
 */
function avatarName(user: WatcherDto['user']): string {
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
}

// Tiny inline debounce — matches the pattern in InviteExternalModal.
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function TaskWatchersSection({
  taskId,
  audience = 'internal',
}: TaskWatchersSectionProps) {
  const isExternal = audience === 'external';
  // Both hooks called unconditionally (stable hook order). Internal audience
  // reads useAuth; external reads the external identity (gated via `enabled`).
  const { user: currentUser } = useAuth();
  const externalMe = useExternalCurrentUser(isExternal).data ?? null;

  const { data, isLoading, selfWatch, selfUnwatch, addWatcher, removeWatcher } =
    useTaskWatchers(taskId, audience);

  // Add-watcher picker state (internal audience only).
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const debouncedQuery = useDebounced(pickerQuery, 150);
  const pickerRef = useRef<HTMLDivElement>(null);

  const candidatesQuery = useWatcherCandidates({
    query: debouncedQuery,
    enabled: !isExternal && isPickerOpen,
  });

  useEffect(() => {
    if (isExternal) return;
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExternal]);

  // "Am I watching?" — match on the correct identity space for the audience.
  const currentUserId = isExternal ? null : (currentUser?.id ?? null);
  const currentExternalId = isExternal ? (externalMe?.id ?? null) : null;
  const canToggle = isExternal ? currentExternalId != null : currentUserId != null;

  // Effective kind — tolerate legacy rows that predate the `kind` field
  // (internal-only shape): a row with a userId is internal.
  const isExternalWatcher = (w: WatcherDto): boolean =>
    w.kind === 'external_user' || (w.userId == null && w.externalUserId != null);

  const meWatching = isExternal
    ? currentExternalId != null &&
      data.some((w) => isExternalWatcher(w) && w.externalUserId === currentExternalId)
    : currentUserId != null &&
      data.some((w) => !isExternalWatcher(w) && w.userId === currentUserId);

  const watchingUserIds = new Set(
    data.filter((w) => !isExternalWatcher(w)).map((w) => w.userId),
  );
  const candidates = (candidatesQuery.data ?? []).filter(
    (c) => !watchingUserIds.has(c.id),
  );

  function handleToggle() {
    if (meWatching) {
      selfUnwatch.mutate();
    } else {
      selfWatch.mutate();
    }
  }

  function handleSelectCandidate(candidateId: number) {
    addWatcher.mutate(candidateId);
    setIsPickerOpen(false);
    setPickerQuery('');
  }

  return (
    <section
      aria-label="Watchers"
      className="pt-3 border-t border-ms-border-subtle"
      data-testid="watchers-section"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ms-text-secondary">
          Watchers
        </h3>
        <div className="flex items-center gap-2">
          {data.length > 0 && (
            <span className="text-xs text-ms-text-secondary tabular-nums">
              {data.length}
            </span>
          )}
          {!isExternal && (
            <div ref={pickerRef} className="relative">
              <button
                type="button"
                onClick={() => setIsPickerOpen((v) => !v)}
                disabled={addWatcher.isPending}
                aria-label="Add watcher"
                data-testid="watchers-add-trigger"
                className="inline-flex items-center gap-1 rounded-full border border-ms-border bg-ms-surface px-2 py-1 text-xs font-medium text-ms-text hover:bg-ms-canvas disabled:opacity-50"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Add watcher
              </button>
              {isPickerOpen && (
                <div className="absolute right-0 z-10 mt-1 w-64 rounded-lg border border-ms-border bg-ms-surface shadow-lg">
                  <input
                    type="text"
                    autoFocus
                    value={pickerQuery}
                    onChange={(e) => setPickerQuery(e.target.value)}
                    placeholder="Search people…"
                    className="w-full rounded-t-lg border-b border-ms-border-subtle px-2 py-1.5 text-sm focus:outline-none"
                  />
                  <div className="max-h-48 overflow-auto">
                    {candidatesQuery.isLoading ? (
                      <p className="px-3 py-2 text-xs text-ms-text-muted italic">
                        Searching…
                      </p>
                    ) : candidates.length === 0 ? (
                      <p className="px-3 py-2 text-xs text-ms-text-muted italic">
                        No matches.
                      </p>
                    ) : (
                      candidates.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handleSelectCandidate(c.id)}
                          disabled={addWatcher.isPending}
                          data-testid={`watchers-candidate-${c.id}`}
                          className="block w-full px-3 py-2 text-left text-sm text-ms-text hover:bg-ms-canvas disabled:opacity-50"
                        >
                          {[c.lastName, c.firstName].filter(Boolean).join(' ') || c.email}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {canToggle && (
            <button
              type="button"
              onClick={handleToggle}
              disabled={selfWatch.isPending || selfUnwatch.isPending}
              aria-label={meWatching ? 'Stop watching' : 'Watch this task'}
              data-testid="watchers-self-toggle"
              className="inline-flex items-center gap-1 rounded-full border border-ms-border bg-ms-surface px-2 py-1 text-xs font-medium text-ms-text hover:bg-ms-canvas disabled:opacity-50"
            >
              {meWatching ? (
                <>
                  <EyeSlashIcon className="w-3.5 h-3.5" />
                  Stop watching
                </>
              ) : (
                <>
                  <EyeIcon className="w-3.5 h-3.5" />
                  Watch
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-ms-text-muted italic">Loading watchers…</p>
      ) : data.length === 0 ? (
        <p className="text-sm text-ms-text-muted italic">No watchers yet.</p>
      ) : (
        <ul className="flex flex-wrap gap-2" data-testid="watchers-list">
          {data.map((watcher) => {
            // Stable testid: internal watchers keep the legacy `watcher-<userId>`
            // id; external watchers use `watcher-ext-<externalUserId>`.
            const external = isExternalWatcher(watcher);
            const watcherKey = external
              ? `ext-${watcher.externalUserId}`
              : `${watcher.userId}`;
            return (
            <li
              key={watcherKey}
              className="inline-flex items-center gap-1.5 rounded-full bg-ms-canvas px-2 py-1 text-xs font-medium text-ms-text"
              data-testid={`watcher-${watcherKey}`}
            >
              {/* aria-hidden: Avatar sets its own aria-label, which would
                  duplicate the adjacent name text for screen readers. The
                  board card has no adjacent text, so it leaves the label on. */}
              <span aria-hidden="true" className="flex shrink-0">
                <Avatar
                  name={avatarName(watcher.user)}
                  colorSeq={watcher.user.avatarColorSeq}
                  avatarUrl={watcher.user.avatarUrl}
                  size="xs"
                />
              </span>
              {displayName(watcher.user)}
              {!external && watcher.userId != null && (
                <button
                  type="button"
                  onClick={() => removeWatcher.mutate(watcher.userId as number)}
                  disabled={removeWatcher.isPending}
                  aria-label={`Remove ${displayName(watcher.user)}`}
                  data-testid={`watcher-remove-${watcher.userId}`}
                  className="text-ms-text-muted hover:text-ms-text disabled:opacity-50"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
